class SearchModal extends HTMLElement {
  constructor() {
    super();

    this.detailsContainer = this.querySelector('details');
    this.summaryToggle = this.querySelector('summary');

    this.detailsContainer.addEventListener(
      'keyup',
      (event) => event.code && event.code.toUpperCase() === 'ESCAPE' && this.close()
    );
    this.summaryToggle.addEventListener(
      'click',
      this.onSummaryClick.bind(this)
    );
    this.querySelector('button[type="button"]').addEventListener(
      'click',
      this.close.bind(this)
    );
    this.querySelector('button[type="reset"]').addEventListener(
      'click',
      this.reset.bind(this)
    );

    this.summaryToggle.setAttribute('role', 'button');

    // Prepare the Best Sellers in the background so they show instantly when search opens
    (window.requestIdleCallback || ((callback) => setTimeout(callback, 2000)))(() => this.loadRecommendations());
  }

  onSummaryClick(event) {
    event.preventDefault();
    event.target.closest('details').hasAttribute('open')
      ? this.close()
      : this.open(event);
  }

  onBodyClick(event) {
    if (!this.contains(event.target) || event.target.classList.contains('modal-overlay')) this.close();
  }

  open() {
    // A width kept from an earlier, wider viewport would add right-side padding for a scrollbar
    // that isn't there (e.g. on mobile). Clear it when there is no scrollbar and no other scroll lock.
    if (getComputedStyle(document.body).overflowY !== 'hidden' && !checkScrollbar()) {
      document.documentElement.style.setProperty('--scrollbar-width', '0px');
    }
    setScrollbarWidth();
    setHeaderBottomPosition();
    
    this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this);
      
    this.detailsContainer.setAttribute('open', true);
    document.body.addEventListener('click', this.onBodyClickEvent);
    document.body.classList.add('search-modal--open');

    trapFocus(
      this.detailsContainer.querySelector('[tabindex="-1"]'),
      this.detailsContainer.querySelector('input:not([type="hidden"])')
    );

    this.loadRecommendations();
    // Lets the page navigation prefetch the Best Sellers product pages (only once search is opened)
    if (this.querySelector('.search-recommendations__products')) document.dispatchEvent(new CustomEvent('search:recommendations-loaded'));
  }

  // Best Sellers products under the search bar: one request per browser session (kept in sessionStorage)
  loadRecommendations() {
    const slot = this.querySelector('[data-search-bestsellers-products]');
    if (!slot || slot.dataset.loaded) return;
    slot.dataset.loaded = 'true';
    const block = slot.closest('[data-search-bestsellers]');

    const url = new URL(slot.dataset.url, window.location.origin);
    url.searchParams.set('section_id', 'search-recommendations');
    const cacheKey = `search-bestsellers:${url}:${window.Shopify?.currency?.active || ''}`;
    let cached = null;
    try { cached = sessionStorage.getItem(cacheKey); } catch (e) {}

    SearchModal.recommendations = SearchModal.recommendations || (cached ? Promise.resolve(cached) : fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then((text) => {
        if (text.includes('search-recommendations__products')) {
          try { sessionStorage.setItem(cacheKey, text); } catch (e) {}
        }
        return text;
      }))
      .then((text) => {
        const doc = new DOMParser().parseFromString(text, 'text/html');
        // Load card stylesheets before showing the grid (avoids an unstyled flash)
        const styles = [...doc.querySelectorAll('link[rel="stylesheet"]')]
          .filter((link) => !document.querySelector(`link[href="${link.getAttribute('href')}"]`))
          .map((link) => new Promise((resolve) => {
            link.onload = link.onerror = resolve;
            document.head.appendChild(link);
          }));
        return Promise.all(styles).then(() => doc);
      });

    SearchModal.recommendations
      .then((doc) => {
        const products = doc.querySelector('.search-recommendations__products');
        if (!products) return block.remove();

        slot.innerHTML = products.outerHTML;
        // Run the card's own image-slider init once (inline scripts don't run via innerHTML)
        const cardScript = products.querySelector('script');
        if (cardScript) {
          const script = document.createElement('script');
          script.textContent = cardScript.textContent;
          slot.appendChild(script);
        }
        // Lets the page navigation prefetch these product pages (only while search is open)
        if (this.detailsContainer.hasAttribute('open')) document.dispatchEvent(new CustomEvent('search:recommendations-loaded'));
      })
      .catch(() => block.remove());
  }

  close() {
    removeTrapFocus();
    this.detailsContainer.removeAttribute('open');
    document.body.removeEventListener('click', this.onBodyClickEvent);
    document.body.classList.remove('search-modal--open');
    document.dispatchEvent(new CustomEvent('searchmodal:close'));
  }

  reset(event) {
    event.preventDefault();
    this.querySelector('input[type="search"]').value = '';
  }
}

customElements.define('search-modal', SearchModal);
